/**
 * Client-side guard for builder hrefs and background URLs. The API already
 * rejects unsafe values on save; this keeps stale drafts and hand-edited JSON
 * from becoming javascript: links in the DOM.
 */

export function safeHref(value: unknown, fallback = '#'): string {
  if (typeof value !== 'string') return fallback;
  const href = value.trim();
  if (!href) return fallback;
  if (/javascript\s*:|<|>/i.test(href)) return fallback;
  if (href.startsWith('/') && !href.startsWith('//')) return href;
  if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return href;
  if (/^https?:\/\//i.test(href)) return href;
  return fallback;
}

/** Returns a CSS backgroundImage value, or undefined when the input is unsafe. */
export function safeBackgroundImage(
  value: unknown,
  kind: 'image' | 'gradient',
): string | undefined {
  if (typeof value !== 'string') return undefined;
  const raw = value.trim();
  if (!raw) return undefined;

  if (kind === 'gradient') {
    if (!/gradient\s*\(/i.test(raw)) return undefined;
    if (/url\s*\(|@import|expression\s*\(|javascript\s*:|<|>|behavior/i.test(raw)) {
      return undefined;
    }
    return raw;
  }

  const href = safeHref(raw, '');
  if (!href || href === '#') return undefined;
  return `url(${JSON.stringify(href)})`;
}
